import { useMediaQuery } from '@mui/material';
import { createTheme } from '@mui/material/styles';
import { SnackbarProvider } from 'notistack';
import { useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import AppLogo from '../src/assets/logo.png';
import ChangeIcons from './change-icons';
import ScrollToTop from './components/scroll-to-top';
import { CollapseDrawerProvider } from './contexts/CollapseDrawerContext';
import { DesktopProvider } from './contexts/DesktopProvider';
import { RightSidebarProvider } from './layouts/right-side-bar/rightSidebarProvider';
import MainComponent from './MainComponent';
import OutComponent from './out-component';
import InitialSetup from './pages/common/initial-setup.component';
import HttpServices from './services/httpService';
import { userModuleURL } from './services/urlService';
import GlobalStyles from './theme/globalStyles';
import { ThemeProvider } from './theme/theme-provider';
import { CommonHelper } from './utils/commonHelper';
import Spinner from './utils/Loader/spinner';
import { ReAuthProvider } from './utils/reAuth/ReAuthContext';
import { LocalStorage } from './utils/storage/local-storage';
import { ToasterProvider } from './utils/toaster/toasterContext';
import { useTranslation } from 'react-i18next';
import { ResellerLimitDialogProvider } from './components/reseller-limit-context/resellerLimitContext';
import DynamicManifest from './DynamicManifest';
import { CookieStorage } from './utils/storage/cookie-storage';
import { LoadLanguage } from './components/languageService';

const InitApplication = () => {
  const customTheme = createTheme({
    breakpoints: {
      values: {
        mdCustom: 950,
      },
    },
  });

  const isDesktop = useMediaQuery(customTheme.breakpoints.up('mdCustom'));

  const isUserLoggedIn = CommonHelper.CheckUserLoggedIn();
  const { t, i18n } = useTranslation();

  // State Variable
  const [appIcon, setAppIcon] = useState('');
  const [isAppFailed, setAppFailed] = useState(false);
  const [isAppLoaded, setAppLoaded] = useState(false);

  useEffect(() => {
    // Print URL params on reload
    const params = new URLSearchParams(window.location.search);
    const afId = params.get('af_id');
    if (afId) {
      setAffiliateCookie(afId);
    }
    if (isUserLoggedIn) {
      getApplicationInit();
    } else {
      getDomainDetails();
    }
  }, []);

  // Set affiliate cookie using HttpServices and urlService
  const setAffiliateCookie = (afId) => {
    HttpServices.Get({
      url: userModuleURL.setAffiliate + `?af_id=${encodeURIComponent(afId)}`,
      successCallback: affiliateSuccessCallback,
      failureCallback: affiliateFailureCallback,
      config: { credentials: 'include' }
    });
  };

  const affiliateSuccessCallback = (data) => {
    if (data?.affiliate_id && data?.expires_on) {
      const expiryDate = new Date(data.expires_on).toUTCString();
      const cookieValue = encodeURIComponent(data.affiliate_id);

      const cookieString = [
        `affiliate_id=${cookieValue}`,
        'path=/',
        'domain=.brand2social.com', // ensures it works across brand2social.com and subdomains
        `expires=${expiryDate}`,
        'Secure',
        'SameSite=None'
      ].join('; ');

      // document.cookie = cookieString;
      CookieStorage.setCookie(cookieString);
    }
  };

  const affiliateFailureCallback = () => {
  };

  const getDomainDetails = () => {
    const initProps = {
      url: userModuleURL.getDomainDetails,
      successCallback: domainSuccessCallback,
      failureCallback: domainFailureCallback,
    };
    HttpServices.Get(initProps);
  };

  const domainSuccessCallback = async (data) => {
    
    try {
      CommonHelper.ClearAppLocalStorage();
      let language = localStorage.getItem("i18nextLng") || data.language || 'english';
      if (language === 'en-US' || language === 'en') language = 'english';
      await LoadLanguage(language);

    // 2. Now change the language in the UI
    await i18n.changeLanguage(language);
      localStorage.setItem('i18nextLng', language);
      if (!data.domain_small_icon) {
      data.domain_small_icon = AppLogo;
    }
    if (!data.domain_large_icon) {
      data.domain_large_icon = AppLogo;
    }

    let app_details = {
      primary_color: data.domain_primary_color,
      secondary_color: data.domain_secondary_color,
      small_icon: data.domain_small_icon,
      large_icon: data.domain_large_icon,
      org_name: data.org_name,
      language: data.language
    };
    
    LocalStorage.setItem('app_details', JSON.stringify(app_details));
    LocalStorage.setItem('domian_details', JSON.stringify(app_details));
      setAppIcon(data.domain_small_icon);
      setAppLoaded(true);
    } catch (error) {
      setAppFailed(true);
    }
  };

  const domainFailureCallback = () => {
    localStorage.clear();
    setAppFailed(true);
  };

  const failureCallback = (message) => {
    localStorage.clear();
    setAppFailed(true);
  };

  const getApplicationInit = () => {
    CommonHelper.AppInitCall(initSuccessCallback, failureCallback);
  };

  const initSuccessCallback = async (data) => {
    
   try {
      CommonHelper.ClearAppLocalStorage();
      let language = localStorage.getItem("i18nextLng") || data.language || 'english';
     // Normalize: i18next sometimes saves 'en-US', but your Go backend expects 'english'
    if (language === 'en-US' || language === 'en') {
      language = 'english';
    }
    // 2. Load and WAIT for the JSON bundle
    const isLoaded = await LoadLanguage(language);
    
    if (isLoaded) {
      await i18n.changeLanguage(language);
      // Force the key in localStorage to match your specific language string
      localStorage.setItem('i18nextLng', language);
    }
      if (!data.org_icon_url) {
      data.org_icon_url = AppLogo;
    }
    let app_details = {
      primary_color: data.org_primary_color,
      secondary_color: data.org_secondary_color,
      small_icon: data.org_icon_url,
      large_icon: data.org_icon_url,
      currency_symbol: data.currency_symbol,
      timezone: data.timezone,
    };
    LocalStorage.setItem('app_details', JSON.stringify(app_details));

       if (data.is_referral_user) {
      LocalStorage.setItem('is_referral_user', 'true');
    }
      setAppIcon(data.org_icon_url);
      let isSetupDone = InitialSetup(data, i18n);
      if (isSetupDone) {
        setAppLoaded(true);
      } else {
        setAppFailed(true);
      }
    } catch (error) {
      setAppFailed(true);
    }
  };

  const handleInitCall = () => {
    setAppLoaded(false);
    getApplicationInit();
  };

  return (
    <>
      {(isAppLoaded || isAppFailed) && <ChangeIcons appIcon={appIcon} />}
      {isAppLoaded && <DynamicManifest />}
      {isAppFailed ? (
        <ToasterProvider>

          <ReAuthProvider>
            <SnackbarProvider maxSnack={6}>
              <CollapseDrawerProvider>
                <BrowserRouter>
                  <DesktopProvider>
                    <ThemeProvider>
                      <ResellerLimitDialogProvider>
                        <RightSidebarProvider>
                          <GlobalStyles />
                          <ScrollToTop />
                          {/* <StyledChart /> */}
                          <OutComponent isDesktop={isDesktop} />
                        </RightSidebarProvider>
                      </ResellerLimitDialogProvider>
                    </ThemeProvider>
                  </DesktopProvider>
                </BrowserRouter>
              </CollapseDrawerProvider>
            </SnackbarProvider>
          </ReAuthProvider>
        </ToasterProvider>
      ) : isAppLoaded ? (
        <ToasterProvider>

          <ReAuthProvider>
            <SnackbarProvider maxSnack={6}>
              <CollapseDrawerProvider>
                <BrowserRouter>
                  <DesktopProvider>
                    <ThemeProvider>
                      <ResellerLimitDialogProvider>
                        <RightSidebarProvider>
                          <GlobalStyles />
                          <ScrollToTop />
                          {/* <StyledChart /> */}
                          <MainComponent isDesktop={isDesktop} handleInitCall={handleInitCall} />
                        </RightSidebarProvider>
                      </ResellerLimitDialogProvider>
                    </ThemeProvider>
                  </DesktopProvider>
                </BrowserRouter>
              </CollapseDrawerProvider>
            </SnackbarProvider>
          </ReAuthProvider>
        </ToasterProvider>
      ) : (
        <Spinner />
      )}
    </>
  );
};

export default InitApplication;
